// Hook for translating landlord content to guest's language
// Uses Edge Function (secure) with Supabase caching to minimize costs
// Gemini API key is stored securely on backend - NOT exposed to frontend!

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { looksUntranslated } from '@/lib/translation';
import { getAuthToken, guestAuthHeaders } from '@/lib/supabase';

// Supabase Edge Function URL for translations
const TRANSLATE_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/translate-content`;

/**
 * Gate for content translation. The guest portal resolves the guest's language
 * asynchronously (booking.guest_language / the guest's pick → load bundle →
 * changeLanguage). Until then i18n still holds the BROWSER language, and every
 * hook used to queue a batch for it — a German-browser guest resolved to English
 * paid twice (Gemini calls + rate budget). Wrap the portal in
 * <ContentTranslationGate value={languageReady}>; hooks outside a gate (landlord
 * pages) are always open. Hooks called in the component that renders the gate
 * pass `enabled` instead.
 */
const ContentTranslationGateContext = createContext(true);
export const ContentTranslationGate = ContentTranslationGateContext.Provider;

/** Options shared by the specialised hooks below. */
export interface TranslationHookOptions {
  /** false = don't request translations yet (show originals). Default true. */
  enabled?: boolean;
  /** Language the host wrote in. Default 'hr'. */
  sourceLanguage?: string | null;
}

interface TranslatedField {
  original: string;
  translated: string;
  isLoading: boolean;
  error?: string;
}

interface TranslatedContent {
  [fieldName: string]: TranslatedField;
}

interface UseTranslatedContentOptions {
  sourceId: string;
  contentType: 'unit' | 'property' | 'recommendation' | 'booking' | 'service' | 'facility';
  sourceLanguage?: string;
  fields: {
    name: string;
    value: string | null | undefined;
  }[];
  /** false = hold requests (originals are shown). Default true. */
  enabled?: boolean;
}

interface UseTranslatedContentResult {
  translations: TranslatedContent;
  isLoading: boolean;
  error: string | null;
  getTranslated: (fieldName: string) => string;
  refresh: () => void;
}

// ── Limits of the translate-content Edge Function ─────────────────────────
// (supabase/functions/translate-content/index.ts). It rejects the WHOLE
// request when any of these is exceeded, so the client must respect them.
/** "Too many fields (max 10)". */
const MAX_FIELDS_PER_REQUEST = 10;
/** A single field over this is rejected — such a field is never sent. */
const MAX_CHARS_PER_FIELD = 4000;
/** Sum of all field lengths in one request ("Request body too large"). */
const MAX_TOTAL_CHARS = 12000;
/** Requests for the same content issued within this window are merged
 *  (one unit = description + notes + equipment/consumable/extra-bed hooks). */
const COALESCE_WINDOW_MS = 25;

// LRU cache with max size to prevent unbounded memory growth
const LRU_MAX_SIZE = 500;
const memoryCache = new Map<string, string>();

/**
 * Short, fast, synchronous hash of a string (djb2). Used in the memory-cache key
 * so that when the SOURCE text changes (landlord edits a note/description), the
 * key changes too and we don't serve a stale translation from this session's
 * cache — we go to the server, which re-translates (it keys on a SHA-256 of the
 * content). Without this, the cache key was contentType:id:field:lang only, so
 * an edit in the same session kept showing the old translation until refresh.
 */
function valueHash(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/** Build the per-field memory-cache key, including a hash of the source value. */
function buildCacheKey(contentType: string, sourceId: string, fieldName: string, value: string, lang: string): string {
  return `${contentType}:${sourceId}:${fieldName}:${lang}:${valueHash(value)}`;
}

// ── Persistent cache (localStorage) ───────────────────────────────────────
// The guest server limit is 60 translate requests / hour per IP, and the
// portal reloads often (pull-to-refresh = full reload, PWA re-opens). Keeping
// successful translations across reloads avoids re-asking for the same text.
// Keys include a hash of the SOURCE text, so an edited text is re-translated.
// Entries expire so a server-side fix of a bad translation reaches the guest.
// v2: v1 could hold "rephrased Croatian" echoes (only exact echoes were kept
// out) — dropped once; the server cache refills it without Gemini calls.
const PERSIST_KEY = 'bookiapp-content-translations-v2';
const LEGACY_PERSIST_KEYS = ['bookiapp-content-translations-v1'];
const PERSIST_MAX = 400;
const PERSIST_TTL_MS = 7 * 24 * 60 * 60 * 1000;
type PersistEntry = [string, number]; // [translation, savedAt]
let persisted: Record<string, PersistEntry> | null = null;
let persistTimer: ReturnType<typeof setTimeout> | null = null;

function loadPersisted(): Record<string, PersistEntry> {
  if (persisted) return persisted;
  persisted = {};
  try {
    for (const k of LEGACY_PERSIST_KEYS) localStorage.removeItem(k);
    const raw = localStorage.getItem(PERSIST_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, PersistEntry>;
      const now = Date.now();
      for (const [k, v] of Object.entries(parsed)) {
        if (Array.isArray(v) && typeof v[0] === 'string' && now - v[1] < PERSIST_TTL_MS) persisted[k] = v;
      }
    }
  } catch {
    // Private mode / corrupted JSON — start empty.
  }
  return persisted;
}

function schedulePersist() {
  if (persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    try {
      const entries = Object.entries(loadPersisted())
        .sort((a, b) => b[1][1] - a[1][1])
        .slice(0, PERSIST_MAX);
      localStorage.setItem(PERSIST_KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch {
      // Quota / blocked storage — the memory cache still works.
    }
  }, 500);
}

function cacheGet(key: string): string | undefined {
  const mem = memoryCache.get(key);
  if (mem !== undefined) return mem;
  const p = loadPersisted()[key];
  if (p) {
    lruSet(key, p[0]);
    return p[0];
  }
  return undefined;
}

function cacheSet(key: string, translated: string, original: string) {
  // Untranslated echoes never get here (flushGroup reports them as failures).
  lruSet(key, translated);
  // An identical result for a non-Croatian source (host writes in English,
  // "WiFi") is a correct answer, but stays memory-only: the server caches it,
  // so the next reload costs no Gemini call anyway.
  if (translated !== original) {
    loadPersisted()[key] = [translated, Date.now()];
    schedulePersist();
  }
}

function cacheDelete(key: string) {
  memoryCache.delete(key);
  const p = loadPersisted();
  if (key in p) {
    delete p[key];
    schedulePersist();
  }
}

function lruSet(key: string, value: string) {
  if (memoryCache.has(key)) memoryCache.delete(key);
  memoryCache.set(key, value);
  if (memoryCache.size > LRU_MAX_SIZE) {
    const firstKey = memoryCache.keys().next().value;
    if (firstKey !== undefined) memoryCache.delete(firstKey);
  }
}

// Call Edge Function for translation (secure - API key is on backend)
async function translateViaEdgeFunction(
  fields: Array<{ name: string; value: string }>,
  sourceId: string,
  contentType: string,
  sourceLanguage: string,
  targetLanguage: string
): Promise<{ translations: Record<string, string>; errors: Record<string, string> }> {
  // translate-content only serves a verified caller: the logged-in landlord
  // (x-clerk-token) or a guest of this stay (x-guest-token / x-guest-code).
  const authToken = getAuthToken();
  const response = await fetch(TRANSLATE_FUNCTION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      ...(authToken ? { 'x-clerk-token': authToken } : {}),
      ...guestAuthHeaders(),
    },
    body: JSON.stringify({
      fields,
      sourceId,
      contentType,
      sourceLanguage,
      targetLanguage,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Translation failed: ${response.status}`);
  }

  const data = await response.json();
  // Per-field failures come back as the ORIGINAL text in `translations` plus
  // an entry in `errors` — those must not be cached as translations.
  return { translations: data.translations || {}, errors: data.errors || {} };
}

/**
 * Split fields into requests the Edge Function accepts: ≤ MAX_FIELDS_PER_REQUEST
 * fields and ≤ MAX_TOTAL_CHARS characters each, unique field names per request.
 * Fields over MAX_CHARS_PER_FIELD are left out by the caller.
 */
function buildBatches(fields: Array<{ name: string; value: string }>): Array<Array<{ name: string; value: string }>> {
  const batches: Array<Array<{ name: string; value: string }>> = [];
  let current: Array<{ name: string; value: string }> = [];
  let chars = 0;
  let names = new Set<string>();
  for (const f of fields) {
    const len = f.value.length;
    if (current.length > 0 && (current.length >= MAX_FIELDS_PER_REQUEST || chars + len > MAX_TOTAL_CHARS || names.has(f.name))) {
      batches.push(current);
      current = [];
      chars = 0;
      names = new Set();
    }
    current.push(f);
    chars += len;
    names.add(f.name);
  }
  if (current.length > 0) batches.push(current);
  return batches;
}

// ── Request coalescing + in-flight dedupe ─────────────────────────────────
// Several hooks translate fields of the SAME content (a unit: main fields,
// amenity notes, equipment notes, consumables, extra beds…) in the same render.
// Each used to fire its own request. Now every field request is queued for a
// few ms per (contentType, sourceId, languages) and sent together in as few
// batches as the server limits allow; identical in-flight fields are shared.
type FieldResult = { translated: string } | { error: string };
interface PendingGroup {
  contentType: string;
  sourceId: string;
  sourceLanguage: string;
  targetLanguage: string;
  fields: Array<{ name: string; value: string; key: string }>;
  resolvers: Map<string, (r: FieldResult) => void>;
}
const pendingGroups = new Map<string, PendingGroup>();
const inFlight = new Map<string, Promise<FieldResult>>();

async function flushGroup(groupKey: string) {
  const group = pendingGroups.get(groupKey);
  pendingGroups.delete(groupKey);
  if (!group) return;
  const settle = (key: string, r: FieldResult) => {
    group.resolvers.get(key)?.(r);
    inFlight.delete(key);
  };
  const batches = buildBatches(group.fields.map(({ name, value }) => ({ name, value })));
  let offset = 0;
  await Promise.all(
    batches.map((batch) => {
      const meta = group.fields.slice(offset, offset + batch.length);
      offset += batch.length;
      return translateViaEdgeFunction(batch, group.sourceId, group.contentType, group.sourceLanguage, group.targetLanguage)
        .then(({ translations, errors }) => {
          for (const f of meta) {
            const tr = translations[f.name];
            if (errors[f.name] || typeof tr !== 'string' || !tr.trim()) {
              settle(f.key, { error: errors[f.name] || 'Translation failed' });
            } else if (looksUntranslated(f.value, tr, group.sourceLanguage, group.targetLanguage)) {
              // Still Croatian ("rephrased") — show the host's own words instead.
              settle(f.key, { error: 'Untranslated echo' });
            } else {
              cacheSet(f.key, tr, f.value);
              settle(f.key, { translated: tr });
            }
          }
        })
        .catch((err: unknown) => {
          // A rejected batch (rate limit, size, network) only fails ITS fields;
          // nothing is cached, so they are retried on the next visit.
          console.error('Translation batch error:', err);
          const message = err instanceof Error ? err.message : 'Translation failed';
          for (const f of meta) settle(f.key, { error: message });
        });
    }),
  );
}

function requestFieldTranslation(
  contentType: string,
  sourceId: string,
  name: string,
  value: string,
  sourceLanguage: string,
  targetLanguage: string,
): Promise<FieldResult> {
  const key = buildCacheKey(contentType, sourceId, name, value, targetLanguage);
  const cached = cacheGet(key);
  if (cached !== undefined) return Promise.resolve({ translated: cached });
  const existing = inFlight.get(key);
  if (existing) return existing;

  const groupKey = `${contentType}|${sourceId}|${sourceLanguage}|${targetLanguage}`;
  let group = pendingGroups.get(groupKey);
  if (!group) {
    group = { contentType, sourceId, sourceLanguage, targetLanguage, fields: [], resolvers: new Map() };
    pendingGroups.set(groupKey, group);
    setTimeout(() => { void flushGroup(groupKey); }, COALESCE_WINDOW_MS);
  }
  const g = group;
  const promise = new Promise<FieldResult>((resolve) => {
    g.resolvers.set(key, resolve);
  });
  g.fields.push({ name, value, key });
  inFlight.set(key, promise);
  return promise;
}

/** Normalise "en-US" → "en" (server cache and prompts use the bare code). */
function baseLanguage(lang: string | undefined | null): string {
  return (lang || 'hr').split('-')[0];
}

interface ResultState {
  /** Request identity these results belong to (content + values + language). */
  key: string;
  done: boolean;
  results: Record<string, FieldResult>;
}

// Main hook
export function useTranslatedContent(
  options: UseTranslatedContentOptions
): UseTranslatedContentResult {
  const { i18n } = useTranslation();
  const gateOpen = useContext(ContentTranslationGateContext);
  const enabled = gateOpen && options.enabled !== false;
  const targetLanguage = baseLanguage(i18n.language);
  const sourceLanguage = baseLanguage(options.sourceLanguage || 'hr');
  const { contentType, sourceId } = options;
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Stabilize fields array to prevent infinite loops
  const fieldsKey = useMemo(
    () => JSON.stringify(options.fields.map(f => ({ name: f.name, value: f.value }))),
    [options.fields]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFields = useMemo(() => options.fields, [fieldsKey]);

  // Identity of the CURRENT request. Results are stored with it and ignored
  // once it changes, so a late answer for the previous unit/language can never
  // land on the new one (the old code keyed results by field name only, and an
  // early "already translating" return dropped the newer language request).
  const requestKey = `${contentType}|${sourceId}|${sourceLanguage}>${targetLanguage}|${fieldsKey}|${refreshCounter}`;
  const needsTranslation = enabled && !!sourceId && targetLanguage !== sourceLanguage;

  const [state, setState] = useState<ResultState>({ key: '', done: true, results: {} });

  useEffect(() => {
    if (!needsTranslation) return;
    const toTranslate = stableFields.filter(
      (f): f is { name: string; value: string } =>
        typeof f.value === 'string' && f.value.trim().length > 0 && f.value.length <= MAX_CHARS_PER_FIELD,
    );
    const uncached = toTranslate.filter(
      (f) => cacheGet(buildCacheKey(contentType, sourceId, f.name, f.value, targetLanguage)) === undefined,
    );
    if (uncached.length === 0) {
      setState({ key: requestKey, done: true, results: {} });
      return;
    }

    let cancelled = false;
    setState({ key: requestKey, done: false, results: {} });
    void Promise.all(
      uncached.map((f) =>
        requestFieldTranslation(contentType, sourceId, f.name, f.value, sourceLanguage, targetLanguage)
          .then((r) => [f.name, r] as const),
      ),
    ).then((pairs) => {
      if (cancelled) return; // stale: unit or language changed meanwhile
      setState({ key: requestKey, done: true, results: Object.fromEntries(pairs) });
    });
    return () => {
      cancelled = true;
    };
  }, [needsTranslation, stableFields, contentType, sourceId, sourceLanguage, targetLanguage, requestKey]);

  const isCurrent = state.key === requestKey;
  const isLoading = needsTranslation && !(isCurrent && state.done);

  const translations = useMemo<TranslatedContent>(() => {
    const out: TranslatedContent = {};
    for (const field of stableFields) {
      const original = field.value || '';
      if (!needsTranslation || !original.trim()) {
        out[field.name] = { original, translated: original, isLoading: false };
        continue;
      }
      // Cached translations show immediately — even on the render right after
      // a unit switch, before the effect runs.
      const cached = cacheGet(buildCacheKey(contentType, sourceId, field.name, original, targetLanguage));
      if (cached !== undefined) {
        out[field.name] = { original, translated: cached, isLoading: false };
        continue;
      }
      const r = isCurrent ? state.results[field.name] : undefined;
      if (r && 'error' in r) {
        out[field.name] = { original, translated: original, isLoading: false, error: r.error };
      } else if (r) {
        out[field.name] = { original, translated: r.translated, isLoading: false };
      } else {
        // Not translated yet: show the original meanwhile.
        out[field.name] = {
          original,
          translated: original,
          isLoading: original.length <= MAX_CHARS_PER_FIELD && !(isCurrent && state.done),
        };
      }
    }
    return out;
  }, [stableFields, needsTranslation, contentType, sourceId, targetLanguage, isCurrent, state]);

  const error = useMemo(() => {
    if (!isCurrent || !state.done) return null;
    const failures = Object.values(state.results).filter((r): r is { error: string } => 'error' in r);
    return failures.length > 0 && failures.length === Object.keys(state.results).length ? failures[0].error : null;
  }, [isCurrent, state]);

  // Helper to get translated value
  const getTranslated = useCallback(
    (fieldName: string): string => {
      return translations[fieldName]?.translated || '';
    },
    [translations]
  );

  // Refresh translations (force re-translate)
  const refresh = useCallback(() => {
    // Clear caches for this content (key includes the value hash).
    for (const field of stableFields) {
      if (!field.value) continue;
      cacheDelete(buildCacheKey(contentType, sourceId, field.name, field.value, targetLanguage));
    }
    setRefreshCounter((c) => c + 1);
  }, [contentType, sourceId, stableFields, targetLanguage]);

  return {
    translations,
    isLoading,
    error,
    getTranslated,
    refresh,
  };
}

// Simpler hook for single field translation
export function useTranslatedField(
  value: string | null | undefined,
  sourceId: string,
  fieldName: string,
  contentType: 'unit' | 'property' | 'recommendation' = 'unit',
  sourceLanguage: string = 'hr'
): { translated: string; isLoading: boolean } {
  const result = useTranslatedContent({
    sourceId,
    contentType,
    sourceLanguage,
    fields: [{ name: fieldName, value }],
  });

  return {
    translated: result.getTranslated(fieldName),
    isLoading: result.translations[fieldName]?.isLoading || false,
  };
}

// Hook for translating all unit content at once
export function useTranslatedUnit(unit: {
  id: string;
  description?: string | null;
  check_in_instructions?: string | null;
  check_out_instructions?: string | null;
  house_rules?: string | null;
  wifi_name?: string | null;
  wifi_password?: string | null;
  parking_instructions?: string | null;
  parking_notes?: string | null;
  parking_after_arrival?: string | null;
  parking_vehicle_restrictions?: string | null;
  // Self check-in fields
  entrance_instructions?: string | null;
  building_access_info?: string | null;
  smart_lock_instructions?: string | null;
  lockbox_location?: string | null;
  key_location?: string | null;
  floor_apartment_info?: string | null;
  // Equipment how-to instructions (kitchen + laundry) — merged into the
  // KitchenSection / BathroomSection topics, so they must be translated too.
  kitchen_instructions?: string | null;
  kitchen_inventory?: string | null;
  dishwasher_instructions?: string | null;
  washing_machine_instructions?: string | null;
  dryer_instructions?: string | null;
  water_heater_instructions?: string | null;
  // Technical/utilities instructions (GuestEquipmentSection) — also translate.
  ac_instructions?: string | null;
  ac_rules?: string | null;
  heating_instructions?: string | null;
  router_reset_instructions?: string | null;
  trash_instructions?: string | null;
  recycling_instructions?: string | null;
  ev_charger_instructions?: string | null;
  router_location?: string | null;
  trash_collection_days?: string | null;
  fuse_box_location?: string | null;
  water_shutoff_location?: string | null;
  first_aid_location?: string | null;
} | null, opts: TranslationHookOptions = {}) {
  return useTranslatedContent({
    sourceId: unit?.id || '',
    contentType: 'unit',
    sourceLanguage: opts.sourceLanguage || 'hr',
    enabled: opts.enabled,
    fields: [
      { name: 'description', value: unit?.description },
      { name: 'check_in_instructions', value: unit?.check_in_instructions },
      { name: 'check_out_instructions', value: unit?.check_out_instructions },
      { name: 'house_rules', value: unit?.house_rules },
      { name: 'parking_instructions', value: unit?.parking_instructions },
      { name: 'parking_notes', value: unit?.parking_notes },
      { name: 'parking_after_arrival', value: unit?.parking_after_arrival },
      { name: 'parking_vehicle_restrictions', value: unit?.parking_vehicle_restrictions },
      // Self check-in fields
      { name: 'entrance_instructions', value: unit?.entrance_instructions },
      { name: 'building_access_info', value: unit?.building_access_info },
      { name: 'smart_lock_instructions', value: unit?.smart_lock_instructions },
      { name: 'lockbox_location', value: unit?.lockbox_location },
      { name: 'key_location', value: unit?.key_location },
      { name: 'floor_apartment_info', value: unit?.floor_apartment_info },
      // Equipment instructions
      { name: 'kitchen_instructions', value: unit?.kitchen_instructions },
      { name: 'kitchen_inventory', value: unit?.kitchen_inventory },
      { name: 'dishwasher_instructions', value: unit?.dishwasher_instructions },
      { name: 'washing_machine_instructions', value: unit?.washing_machine_instructions },
      { name: 'dryer_instructions', value: unit?.dryer_instructions },
      { name: 'water_heater_instructions', value: unit?.water_heater_instructions },
      // Technical/utilities instructions
      { name: 'ac_instructions', value: unit?.ac_instructions },
      { name: 'ac_rules', value: unit?.ac_rules },
      { name: 'heating_instructions', value: unit?.heating_instructions },
      { name: 'router_reset_instructions', value: unit?.router_reset_instructions },
      { name: 'trash_instructions', value: unit?.trash_instructions },
      { name: 'recycling_instructions', value: unit?.recycling_instructions },
      { name: 'ev_charger_instructions', value: unit?.ev_charger_instructions },
      // Short location/schedule notes the host types in their own language
      { name: 'router_location', value: unit?.router_location },
      { name: 'trash_collection_days', value: unit?.trash_collection_days },
      { name: 'fuse_box_location', value: unit?.fuse_box_location },
      { name: 'water_shutoff_location', value: unit?.water_shutoff_location },
      { name: 'first_aid_location', value: unit?.first_aid_location },
    ],
  });
}

// Hook for translating property content
export function useTranslatedProperty(property: {
  id: string;
  description?: string | null;
} | null, opts: TranslationHookOptions = {}) {
  return useTranslatedContent({
    sourceId: property?.id || '',
    contentType: 'property',
    sourceLanguage: opts.sourceLanguage || 'hr',
    enabled: opts.enabled,
    fields: [
      { name: 'description', value: property?.description },
    ],
  });
}

/**
 * Translate per-amenity notes for a unit. Each note is a field named
 * `amenity_note.<amenityId>` so it caches independently. Returns a map of
 * amenityId -> translated note (falls back to original while loading).
 */
export function useTranslatedAmenityNotes(
  unitId: string | null | undefined,
  notes: Record<string, string> | null | undefined,
  opts: TranslationHookOptions = {},
): Record<string, string> {
  const entries = Object.entries(notes ?? {});
  const fields = entries.map(([id, value]) => ({ name: `amenity_note.${id}`, value }));
  const result = useTranslatedContent({
    sourceId: unitId || '',
    contentType: 'unit',
    sourceLanguage: opts.sourceLanguage || 'hr',
    enabled: opts.enabled,
    fields,
  });
  return useMemo(() => {
    const out: Record<string, string> = {};
    for (const [id] of entries) out[id] = result.getTranslated(`amenity_note.${id}`);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(notes), result.translations]);
}

/**
 * Translate the free-text notes on a unit's extra equipment list. Each note is
 * a field `equipment_note.<itemId>` so it caches per item. Returns a map of
 * itemId -> translated note (original while loading).
 */
export function useTranslatedEquipmentNotes(
  unitId: string | null | undefined,
  equipment: Array<{ item: string; notes?: string }> | null | undefined,
  opts: TranslationHookOptions = {},
): Record<string, string> {
  const withNotes = (equipment ?? []).filter((e) => e.notes && e.notes.trim());
  const fields = withNotes.map((e) => ({ name: `equipment_note.${e.item}`, value: e.notes }));
  const result = useTranslatedContent({
    sourceId: unitId || '',
    contentType: 'unit',
    sourceLanguage: opts.sourceLanguage || 'hr',
    enabled: opts.enabled,
    fields,
  });
  return useMemo(() => {
    const out: Record<string, string> = {};
    for (const e of withNotes) out[e.item] = result.getTranslated(`equipment_note.${e.item}`);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(equipment), result.translations]);
}

/**
 * Translate notes on a unit's consumables. Each note is a field
 * `consumable_note.<itemId>` so it caches per item. Returns itemId -> translated.
 */
export function useTranslatedConsumableNotes(
  unitId: string | null | undefined,
  consumables: Array<{ item: string; note?: string }> | null | undefined,
  opts: TranslationHookOptions = {},
): Record<string, string> {
  const withNotes = (consumables ?? []).filter((c) => c.note && c.note.trim());
  const fields = withNotes.map((c) => ({ name: `consumable_note.${c.item}`, value: c.note }));
  const result = useTranslatedContent({
    sourceId: unitId || '',
    contentType: 'unit',
    sourceLanguage: opts.sourceLanguage || 'hr',
    enabled: opts.enabled,
    fields,
  });
  return useMemo(() => {
    const out: Record<string, string> = {};
    for (const c of withNotes) out[c.item] = result.getTranslated(`consumable_note.${c.item}`);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(consumables), result.translations]);
}

/**
 * Translate extra-bed notes for a unit: the general note + per-type setup notes.
 * Fields: `extrabed_note.general` and `extrabed_note.<bedType>`. Returns
 * { general, byType: { type -> translated } } (originals while loading).
 */
export function useTranslatedExtraBedNotes(
  unitId: string | null | undefined,
  generalNote: string | null | undefined,
  typeNotes: Record<string, string> | null | undefined,
  opts: TranslationHookOptions = {},
): { general: string; byType: Record<string, string> } {
  const typeEntries = Object.entries(typeNotes ?? {}).filter(([, v]) => v && v.trim());
  const fields = [
    { name: 'extrabed_note.general', value: generalNote },
    ...typeEntries.map(([type, value]) => ({ name: `extrabed_note.${type}`, value })),
  ];
  const result = useTranslatedContent({
    sourceId: unitId || '',
    contentType: 'unit',
    sourceLanguage: opts.sourceLanguage || 'hr',
    enabled: opts.enabled,
    fields,
  });
  return useMemo(() => {
    const byType: Record<string, string> = {};
    for (const [type] of typeEntries) byType[type] = result.getTranslated(`extrabed_note.${type}`);
    return { general: result.getTranslated('extrabed_note.general'), byType };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalNote, JSON.stringify(typeNotes), result.translations]);
}

/** What useTranslatedUnitLayout returns — each map falls back to the original. */
export interface TranslatedUnitLayout {
  /** bathroom id -> translated bathroom name (only bathrooms the host named). */
  bathroomNames: Record<string, string>;
  /** General bathroom note. */
  bathroomNotes: string;
  /** sleeping room id -> translated room name (only rooms the host named). */
  roomNames: Record<string, string>;
  /** Kitchen note (unit.details.kitchen.notes). */
  kitchenNotes: string;
}

/**
 * Translate the host-written NAMES and NOTES inside the structured unit layout
 * (bathroom names + notes, sleeping-room names, kitchen notes) — they were the
 * last host text the guest still read untranslated. Fields: `bathroom_name.<id>`,
 * `bathroom_notes`, `room_name.<id>`, `kitchen_notes` (contentType 'unit', so
 * they share the unit's batch).
 */
export function useTranslatedUnitLayout(
  unit: {
    id: string;
    details?: {
      bathrooms?: { bathrooms?: Array<{ id: string; name?: string }>; notes?: string } | null;
      kitchen?: { notes?: string } | null;
    } | null;
    sleeping_config?: { rooms?: Array<{ id: string; name?: string }> } | null;
  } | null | undefined,
  opts: TranslationHookOptions = {},
): TranslatedUnitLayout {
  const bathrooms = (unit?.details?.bathrooms?.bathrooms ?? []).filter((b) => b.name && b.name.trim());
  const rooms = (unit?.sleeping_config?.rooms ?? []).filter((r) => r.name && r.name.trim());
  const bathroomNotes = unit?.details?.bathrooms?.notes;
  const kitchenNotes = unit?.details?.kitchen?.notes;
  const fields = [
    ...bathrooms.map((b) => ({ name: `bathroom_name.${b.id}`, value: b.name })),
    { name: 'bathroom_notes', value: bathroomNotes },
    ...rooms.map((r) => ({ name: `room_name.${r.id}`, value: r.name })),
    { name: 'kitchen_notes', value: kitchenNotes },
  ];
  const result = useTranslatedContent({
    sourceId: unit?.id || '',
    contentType: 'unit',
    sourceLanguage: opts.sourceLanguage || 'hr',
    enabled: opts.enabled,
    fields,
  });
  return useMemo(() => {
    const bathroomNames: Record<string, string> = {};
    for (const b of bathrooms) bathroomNames[b.id] = result.getTranslated(`bathroom_name.${b.id}`);
    const roomNames: Record<string, string> = {};
    for (const r of rooms) roomNames[r.id] = result.getTranslated(`room_name.${r.id}`);
    return {
      bathroomNames,
      bathroomNotes: result.getTranslated('bathroom_notes'),
      roomNames,
      kitchenNotes: result.getTranslated('kitchen_notes'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(fields), result.translations]);
}

/**
 * Translate the notes the host wrote on custom amenities of a unit
 * (unit_custom_amenities.notes). Field `custom_amenity_note.<amenityId>`.
 * Returns amenityId -> translated note.
 */
export function useTranslatedCustomAmenityNotes(
  unitId: string | null | undefined,
  items: Array<{ amenity_id: string; notes?: string | null }> | null | undefined,
  opts: TranslationHookOptions = {},
): Record<string, string> {
  const withNotes = (items ?? []).filter((a) => a.notes && a.notes.trim());
  const fields = withNotes.map((a) => ({ name: `custom_amenity_note.${a.amenity_id}`, value: a.notes }));
  const result = useTranslatedContent({
    sourceId: unitId || '',
    contentType: 'unit',
    sourceLanguage: opts.sourceLanguage || 'hr',
    enabled: opts.enabled,
    fields,
  });
  return useMemo(() => {
    const out: Record<string, string> = {};
    for (const a of withNotes) out[a.amenity_id] = result.getTranslated(`custom_amenity_note.${a.amenity_id}`);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(fields), result.translations]);
}

/**
 * Translate the notes on a booking's scheduled cleanings. Each note is a field
 * `cleaning_note.<index>` so it caches per row. Returns index -> translated note.
 */
export function useTranslatedCleaningNotes(
  bookingId: string | null | undefined,
  cleanings: Array<{ note?: string }> | null | undefined,
  opts: TranslationHookOptions = {},
): Record<number, string> {
  const list = cleanings ?? [];
  const fields = list.map((c, i) => ({ name: `cleaning_note.${i}`, value: c.note }));
  const result = useTranslatedContent({
    sourceId: bookingId || '',
    contentType: 'booking',
    sourceLanguage: opts.sourceLanguage || 'hr',
    enabled: opts.enabled,
    fields,
  });
  return useMemo(() => {
    const out: Record<number, string> = {};
    list.forEach((_, i) => { out[i] = result.getTranslated(`cleaning_note.${i}`); });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(cleanings), result.translations]);
}

// Hook for translating booking content (payment messages, etc.)
export function useTranslatedBooking(booking: {
  id: string;
  payment_due_message?: string | null;
  notes?: string | null;
} | null, opts: TranslationHookOptions = {}) {
  return useTranslatedContent({
    sourceId: booking?.id || '',
    contentType: 'booking',
    sourceLanguage: opts.sourceLanguage || 'hr',
    enabled: opts.enabled,
    fields: [
      { name: 'payment_due_message', value: booking?.payment_due_message },
    ],
  });
}

/**
 * The host's personal note on a cancelled booking (BookingCancelledScreen).
 * Field `cancellation_message` on the booking. Returns the translated text
 * (the original while loading / without an id).
 */
export function useTranslatedCancellationMessage(
  bookingId: string | null | undefined,
  message: string | null | undefined,
  opts: TranslationHookOptions = {},
): string {
  const result = useTranslatedContent({
    sourceId: bookingId || '',
    contentType: 'booking',
    sourceLanguage: opts.sourceLanguage || 'hr',
    enabled: opts.enabled,
    fields: [{ name: 'cancellation_message', value: message }],
  });
  return result.getTranslated('cancellation_message') || message || '';
}

// Hook for translating a single recommendation
export function useTranslatedRecommendation(recommendation: {
  id: string;
  name?: string | null;
  description?: string | null;
  how_to_get_there?: string | null;
  insider_tip?: string | null;
  personal_note?: string | null;
} | null) {
  return useTranslatedContent({
    sourceId: recommendation?.id || '',
    contentType: 'recommendation',
    sourceLanguage: 'hr',
    // The place NAME is a proper noun ("Konoba Marjan") and stays as written —
    // machine-translating it ("Marjan Tavern") makes it unfindable on the street.
    fields: [
      { name: 'description', value: recommendation?.description },
      { name: 'personal_note', value: recommendation?.personal_note },
      { name: 'how_to_get_there', value: recommendation?.how_to_get_there },
      { name: 'insider_tip', value: recommendation?.insider_tip },
    ],
  });
}

// Hook for translating a single service
export function useTranslatedService(service: {
  id: string;
  name?: string | null;
  description?: string | null;
} | null) {
  return useTranslatedContent({
    sourceId: service?.id || '',
    contentType: 'service',
    sourceLanguage: 'hr',
    fields: [
      { name: 'name', value: service?.name },
      { name: 'description', value: service?.description },
    ],
  });
}

// Hook for translating a single facility
export function useTranslatedFacility(facility: {
  id: string;
  name?: string | null;
  description?: string | null;
  location_description?: string | null;
  usage_instructions?: string | null;
} | null) {
  return useTranslatedContent({
    sourceId: facility?.id || '',
    contentType: 'facility',
    sourceLanguage: 'hr',
    fields: [
      { name: 'name', value: facility?.name },
      { name: 'description', value: facility?.description },
      { name: 'location_description', value: facility?.location_description },
      { name: 'usage_instructions', value: facility?.usage_instructions },
    ],
  });
}

export default useTranslatedContent;
